/* ============================================================================
   tne-formulario.js — Validação client-side + persistência localStorage
   DistribuidorTopBioEuropa | Site institucional B2B

   - Validação inline em tempo real (blur + ao submeter)
   - Mensagens de erro abaixo de cada campo
   - Auto-focus + smooth-scroll para o primeiro campo inválido
   - Loading state no botão durante submit
   - Persiste rascunho em localStorage; limpa em sucesso
   - Mostra rascunho recuperado se a página recarregar antes do submit
   ============================================================================ */

(function () {
  'use strict';

  const STORAGE_KEY = 'tne-form-candidatura-rascunho';
  const FORM_ID = 'tne-form-candidatura';
  // TTL — rascunhos com mais de 24h são descartados ao restaurar (item #2
  // da auditoria 2026-05-20: dados pessoais em localStorage sem expiração
  // criam vector RGPD + risco de exfiltração via XSS de rascunhos antigos
  // que a visitante esqueceu).
  const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function init() {
    const form = document.getElementById(FORM_ID);
    if (!form) return;

    restoreDraft(form);
    attachAutosave(form);
    attachValidation(form);
    attachSubmit(form);
  }

  /* --------------------------------------------------------------------------
     Validação por campo
     -------------------------------------------------------------------------- */
  function getFieldError(field) {
    if (field.disabled) return '';

    if (field.type === 'radio') {
      const group = document.getElementsByName(field.name);
      const required = Array.prototype.some.call(group, function (r) { return r.required; });
      if (!required) return '';
      const checked = Array.prototype.some.call(group, function (r) { return r.checked; });
      return checked ? '' : 'Por favor selecione uma opção.';
    }

    if (field.type === 'checkbox') {
      if (!field.required) return '';
      return field.checked ? '' : 'É necessário aceitar este consentimento.';
    }

    const value = (field.value || '').trim();

    if (field.required && value === '') {
      return 'Este campo é obrigatório.';
    }

    if (field.type === 'email' && value !== '') {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!ok) return 'Por favor introduza um email válido.';
    }

    if (field.type === 'tel' && value !== '') {
      const digits = value.replace(/\D/g, '');
      if (digits.length < 7) return 'Telefone parece demasiado curto.';
    }

    return '';
  }

  function setFieldError(field, message) {
    const wrapper = field.closest('.tne-form__field') || field.closest('.tne-form__checkbox') || field.parentElement;
    if (!wrapper) return;

    let errorEl = wrapper.querySelector('.tne-form__field-error');

    if (message) {
      if (field.type !== 'radio' && field.type !== 'checkbox') {
        field.classList.add('is-invalid');
        field.setAttribute('aria-invalid', 'true');
      }
      if (!errorEl) {
        errorEl = document.createElement('span');
        errorEl.className = 'tne-form__field-error';
        wrapper.appendChild(errorEl);
      }
      errorEl.textContent = message;
    } else {
      if (field.type !== 'radio' && field.type !== 'checkbox') {
        field.classList.remove('is-invalid');
        field.removeAttribute('aria-invalid');
      }
      if (errorEl) errorEl.remove();
    }
  }

  function attachValidation(form) {
    const fields = $$('input, textarea, select', form);

    fields.forEach(function (field) {
      field.addEventListener('blur', function () {
        const msg = getFieldError(field);
        setFieldError(field, msg);
      });

      field.addEventListener('input', function () {
        if (field.classList.contains('is-invalid') || field.getAttribute('aria-invalid') === 'true') {
          const msg = getFieldError(field);
          setFieldError(field, msg);
        }
      });

      if (field.type === 'radio' || field.type === 'checkbox') {
        field.addEventListener('change', function () {
          const msg = getFieldError(field);
          setFieldError(field, msg);
        });
      }
    });
  }

  function validateAll(form) {
    const fields = $$('input, textarea, select', form);
    let firstInvalid = null;
    const seenRadios = {};

    fields.forEach(function (field) {
      if (field.type === 'radio') {
        if (seenRadios[field.name]) return;
        seenRadios[field.name] = true;
      }
      const msg = getFieldError(field);
      setFieldError(field, msg);
      if (msg && !firstInvalid) firstInvalid = field;
    });

    return firstInvalid;
  }

  /* --------------------------------------------------------------------------
     Submit handler — loading state + scroll para erros
     -------------------------------------------------------------------------- */
  function attachSubmit(form) {
    form.addEventListener('submit', function (e) {
      // Honeypot anti-spam (item #4 auditoria 2026-05-20). Humanos não veem
      // este campo (fora do viewport via CSS); bots preenchem-no quase
      // sempre. Se vier com valor, cancela silenciosamente: nem erro
      // visual, nem redirect — o bot crê que submeteu com sucesso.
      const hp = form.querySelector('#tne-hp-website');
      if (hp && hp.value.trim() !== '') {
        e.preventDefault();
        return;
      }

      const firstInvalid = validateAll(form);

      if (firstInvalid) {
        e.preventDefault();
        try {
          firstInvalid.focus({ preventScroll: true });
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (err) {
          firstInvalid.focus();
        }
        return;
      }

      const submitBtn = form.querySelector('[data-tne-form-submit]');
      if (submitBtn) {
        submitBtn.dataset.loading = 'true';
        submitBtn.disabled = true;
      }
    });
  }

  /* --------------------------------------------------------------------------
     Persistência localStorage (rascunho)
     -------------------------------------------------------------------------- */
  function getSerializableFields(form) {
    return $$('input, textarea, select', form).filter(function (f) {
      if (!f.name) return false;
      if (f.type === 'hidden') return false;
      if (f.type === 'submit' || f.type === 'button') return false;
      if (f.type === 'password') return false;
      if (f.id === 'tne-hp-website') return false; // honeypot — nunca guardar
      return true;
    });
  }

  function snapshot(form) {
    const data = {};
    const fields = getSerializableFields(form);
    fields.forEach(function (field) {
      if (field.type === 'checkbox') {
        data[field.name] = field.checked;
      } else if (field.type === 'radio') {
        if (field.checked) data[field.name] = field.value;
      } else {
        data[field.name] = field.value;
      }
    });
    return data;
  }

  function attachAutosave(form) {
    let saveTimer = null;
    function debouncedSave() {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(function () {
        try {
          // Envelope com savedAt — permite descartar ao restaurar se a
          // visitante voltar mais de 24h depois (RGPD + redução de
          // exposição em browser partilhado / XSS de rascunhos antigos).
          const envelope = {
            savedAt: Date.now(),
            data: snapshot(form)
          };
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
        } catch (err) {
          /* localStorage indisponível ou cheio — ignorar silenciosamente */
        }
      }, 400);
    }

    $$('input, textarea, select', form).forEach(function (field) {
      field.addEventListener('input', debouncedSave);
      field.addEventListener('change', debouncedSave);
    });
  }

  function restoreDraft(form) {
    let raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      return;
    }
    if (!raw) return;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      return;
    }

    if (!parsed || typeof parsed !== 'object') return;

    // Formato legacy (pré-2026-05-20) — payload era directamente o snapshot
    // sem savedAt. Não há como saber a idade, descartar por precaução.
    if (typeof parsed.savedAt !== 'number' || !parsed.data) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      return;
    }

    // Rascunho expirado — limpa silenciosamente e ignora.
    if (Date.now() - parsed.savedAt > STORAGE_TTL_MS) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      return;
    }

    const data = parsed.data;
    if (typeof data !== 'object') return;

    const fields = getSerializableFields(form);
    fields.forEach(function (field) {
      if (!(field.name in data)) return;
      const v = data[field.name];

      if (field.type === 'checkbox') {
        field.checked = !!v;
      } else if (field.type === 'radio') {
        field.checked = (field.value === v);
      } else {
        field.value = v == null ? '' : v;
      }
    });
  }

  function clearDraft() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (err) {}
  }

  /* --------------------------------------------------------------------------
     Limpar rascunho quando o Shopify renderiza o estado de sucesso.
     O bloco .tne-form__success aparece no DOM após /contact retornar
     posted_successfully, portanto detectá-lo é fiável.
     -------------------------------------------------------------------------- */
  function checkSuccessState() {
    if (document.querySelector('.tne-form__success')) {
      clearDraft();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      checkSuccessState();
    });
  } else {
    init();
    checkSuccessState();
  }
})();
